<template>
  <div class="c-blockquote-carousel">
    <div class="c-blockquote-carousel__info" v-if="fields && fields.heading && fields.desc">
      <sc-text
        tag="h3"
        class="c-blockquote-carousel__title"
        v-if="fields && fields.heading"
        :field="fields.heading"
      />
      <sc-text
        tag="p"
        class="c-blockquote-carousel__desc"
        v-if="fields && fields.desc"
        :field="fields.desc"
      />
    </div>
    <template v-if="isEditing">
      <div class="c-blockquote-carousel__quote">
        <sc-placeholder name="jss-blockquotes" :rendering="rendering" />
      </div>
    </template>
    <template v-else>
      <div
        class="c-blockquote-carousel__quote"
        :class="{
          'full-width': fields.heading.value === '' && fields.desc.value === ''
        }"
      >
        <Splide
          ref="splide"
          @splide:resize="onResize"
          @splide:active="onActive"
          :options="options"
          aria-label="Quotes"
        >
          <SplideSlide
            ref="SplideSlide"
            v-for="(quote, index) in $options.computed.quotePlaceholder"
            :is="quote"
            :key="`quote${index}`"
          >
            <component :is="quote" :key="`quote${index}`" />
          </SplideSlide>
        </Splide>
      </div>
    </template>
  </div>
</template>

<script>
import { Text, Placeholder } from '@sitecore-jss/sitecore-jss-vue';
import { ref, reactive } from 'vue';
import { Splide, SplideSlide } from '@splidejs/vue-splide';
import '@splidejs/vue-splide/css/core';

export default {
  name: 'CIS-Blockquote-Carousel',
  components: {
    ScText: Text,
    ScPlaceholder: Placeholder,
    Splide,
    SplideSlide
  },
  props: {
    rendering: {
      type: Object
    },
    fields: {
      type: Object
    }
  },
  withPlaceholder() {
    return {
      placeholders: [
        {
          placeholder: 'jss-blockquotes',
          computedPropName: 'quotePlaceholder'
        }
      ]
    };
  },
  setup() {
    const splide = ref();
    const SplideSlide = ref();

    const options = reactive({
      type: 'fade',
      arrows: false,
      rewind: true,
      autoplay: true,
      speed: 350,
      interval: 8000
    });

    const onActive = (item) => {
      const slideHeight = SplideSlide?.value[item.index]?.$el?.children[0]?.offsetHeight;
      options.height = slideHeight + 'px';
    };

    const onResize = (item) => {
      const slideHeight = SplideSlide?.value[item.index]?.$el?.children[0]?.offsetHeight;
      options.height = slideHeight + 'px';
    };

    return { splide, SplideSlide, options, onActive, onResize };
  },
  computed: {
    isEditing() {
      // this.$jss is defined on the Vue instance by the SitecoreJssPlugin and provides
      // reactive access to the `sitecoreContext` provided in layout service data.
      return this.$jss.sitecoreContext().pageEditing;
    }
  }
};
</script>
<style lang="scss" scoped>
.c-blockquote-carousel {
  padding: 3rem 0;
  margin-bottom: 2rem;

  @include tablet-up {
    display: flex;
    flex-wrap: wrap;
    width: 100%;
  }

  &__info {
    width: 100%;

    @include tablet-up {
      width: calc(30% - 4rem);
      margin-right: 4rem;
    }

    @include desktop-up {
      width: calc(30% - 8rem);
      margin-right: 8rem;
    }
  }

  &__title {
    @include font-styles('Arial', 2rem, 200, normal, 2.6rem, 0, $dark-blue);
    margin: 0 0 2rem 0;

    @include tablet-up {
      font-size: 2.8rem;
      line-height: 3.5rem;
    }
  }

  &__quote {
    position: relative;
    width: 100%;
    padding-left: 4.5rem;
    padding-right: 2rem;

    @include tablet-up {
      width: 70%;
      padding-left: 8rem;
      padding-right: 0;
    }

    &.full-width {
      width: 100%;
    }

    &:before {
      content: '';
      display: block;
      position: absolute;
      top: 0.8rem;
      left: 0;
      width: 2.95rem;
      height: 2.27rem;
      background-size: cover;
      -webkit-mask: url(~@/assets/images/hp_blockquote.png);
      mask: url(~@/assets/images/hp_blockquote.png);
      mask-size: cover;
      background-color: $orange;

      @include tablet-up {
        top: 1rem;
        width: 5.2rem;
        height: 4rem;
      }
    }
  }
}
</style>
